import React, {createContext, FC, useContext, useEffect, useMemo, useState} from "react";
import {KCTEventDto} from "../types/contracts";
import apiService from "../services/api-service";

type Props = {
  children: React.ReactNode;
}

type EventsProviderModel = {
  setEvents: React.Dispatch<React.SetStateAction<KCTEventDto[] | null>>;
  events: KCTEventDto[] | null;
  selectedEventId: string | null;
  setSelectedEventId: (eventId: string) => void;
  selectedEvent: KCTEventDto | null;
  setSelectedEvent: React.Dispatch<React.SetStateAction<KCTEventDto | null>>;
  setIsLoading: React.Dispatch<React.SetStateAction<boolean>>;
  isLoading: boolean;
}

const EventsProviderContext = createContext<EventsProviderModel | undefined>(undefined)

export const EventsProvider: FC<Props> = ({ children}) => {

  const [events, setEvents] = useState<KCTEventDto[] | null>(null);
  const [selectedEventId, setSelectedEventId] = useState<string | null>(null);
  const [selectedEvent, setSelectedEvent] = useState<KCTEventDto | null>(null);
  const [isLoading, setIsLoading] = useState(false);


  useMemo(() => {
      if (selectedEventId && events) {
          setSelectedEvent((events?.find((event) => event.id === selectedEventId) ?? null))
      }
  }, [selectedEventId, events])

  const loadEvents = async () => {
    try {
      setIsLoading(true);
      const {data} = await apiService.getEvents() as { data: KCTEventDto[] };
      setEvents(data);

      // default select event in <select>
      const currentEvent = data.find((event) => event.isCurrentEvent);
      if (!selectedEventId && currentEvent) {
        setSelectedEventId(currentEvent.id);
      }
    } catch (error) {
      console.error("Failed to fetch events", error);
    }
    finally {
      setIsLoading(false);
    }
  };

  // load events only here // use react-query for caching in future
  useEffect(() => {
    loadEvents();
  }, []);

  return <EventsProviderContext.Provider
    value={{
      setEvents,
      events,
      selectedEventId,
      setSelectedEventId,
      selectedEvent,
      setSelectedEvent,
      setIsLoading,
      isLoading,
    }}
  >
    {children}
  </EventsProviderContext.Provider>
}

export const useEventsContext = () => {
  const userContext = useContext(EventsProviderContext);
  if (!userContext) {
    throw new Error('useLoadEventsContext must be used with LoadEventsProvider');
  }
  return userContext;
}